// The entry point form (spec 11 6.15.10, mock n05c's layout): name, tier and region, add-ons with their costs, domains,
// routes with their origin and origin access, the dry run's diagnostics at their fields and its cost line, and Save with
// the whole `settings.entryPoints` array and If-Match (a 412 merges this entry point into the newer array).
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fetchAllPages, isApiError, type Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { queryKeys, useAttachments, useEntryPointStatus, useNetworkKinds } from '../../api/queries';
import type {
  CommitmentNotConfirmedDetails,
  Diagnostic,
  EdgeKind,
  EntryPointAddOnSettings,
  EntryPointSettings,
  Network,
  NetworkValidation,
  OriginAccessMode,
  PatchNetworkRequest,
} from '../../api/types';
import { useToast } from '../../components/toast';
import { Dialog, FieldMessages, IconButton } from '../../components/ui';
import { strings } from '../../strings';
import { CommitmentDialog } from './CommitmentDialog';
import {
  ACCESS_ORDER,
  ACCESS_STRENGTH,
  addOnEnabled,
  commitmentOf,
  dayWords,
  edgeOf,
  expectedConfirmation,
  heldReason,
  isWaf,
  offeredModes,
  preselectedMode,
} from './helpers';

const e = strings.networks.entryPoints;
const aw = e.aws;
const gc = e.gcp;
const cf = e.cloudflare;
const ZONE_PATTERN = /^Z[A-Z0-9]{1,31}$/;
/** Feature spec 17, phase 8: a Cloudflare zone ID (spec 18 5.1 `dnsZone`). */
const CLOUDFLARE_ZONE_PATTERN = /^[0-9a-f]{32}$/;
const ID_PATTERN = /^[a-z][a-z0-9-]{0,30}$/;
const HOST_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const PATH_PATTERN = /^\/[^*\s]*(\/\*)?$|^\/\*$/;
const PLATFORM_OF_TYPE: Record<string, string> = {
  'compute.webapp': 'app-service',
  'compute.function': 'functions-flex',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10" → "Oct 2026". */
function asOfWords(asOf: string): string {
  const [year, month] = asOf.split('-');
  return month ? `${MONTHS[Number(month) - 1] ?? month} ${year}` : asOf;
}

/** The add-ons as the form keeps them: every add-on of the kind explicit; paid ones never on by themselves (D13). */
function initialAddOns(kind: EdgeKind, ep: EntryPointSettings | null, tier: string | null) {
  const addOns: Record<string, EntryPointAddOnSettings> = {};
  for (const a of kind.addOns) {
    const saved = ep?.addOns?.[a.id];
    const tierOk = !a.requiresTier || a.requiresTier === tier;
    addOns[a.id] = saved
      ? { ...saved }
      : {
          // The WAF add-ons follow their default although AWS WAF has a fee (spec 18 5.1: on by default).
          enabled: ep
            ? addOnEnabled(ep, kind, a.id)
            : a.default && (!a.cost || isWaf(a.id)) && !a.commitment && !heldReason(a) && tierOk,
        };
    if (a.id === 'waf' && !saved)
      addOns.waf = { ...addOns.waf, mode: 'prevention', rateLimitPerMinute: 2000 };
    // AWS WAF (phase 5): managed rules on, Bot Control off (priced per request), the rate rule at 2,000.
    if (a.id === 'aws-waf' && !saved)
      addOns['aws-waf'] = {
        ...addOns['aws-waf'],
        mode: 'prevention',
        managedRules: true,
        botProtection: false,
        rateLimitPerMinute: 2000,
      };
    // Cloudflare (phase 8): the managed ruleset by plan, no bot protection (a zone setting), the rate rule at 2,000
    // (none on Free, user decision 2026-11-04); DDoS protection is always on.
    if (kind.kind === 'cloudflare' && a.id === 'waf' && !saved)
      addOns.waf = {
        ...addOns.waf,
        mode: 'prevention',
        managedRules: true,
        botProtection: false,
        rateLimitPerMinute: tier === 'free' ? 0 : 2000,
      };
    if (kind.kind === 'cloudflare' && a.id === 'ddos') addOns.ddos = { enabled: true };
    // Cloud Armor (phase 7): the preconfigured SQL injection and XSS rules on, no bot management, the rate rule at 2,000.
    if (a.id === 'cloud-armor' && !saved)
      addOns['cloud-armor'] = {
        ...addOns['cloud-armor'],
        mode: 'prevention',
        managedRules: true,
        botProtection: false,
        rateLimitPerMinute: 2000,
      };
  }
  return addOns;
}

interface OriginOption {
  deploymentId: string;
  deploymentName: string;
  resourceId: string;
  platform: string;
  label: string;
  region: string;
  unavailable: string | null;
  /** Feature spec 17, phase 8: the other web apps sharing an ECS Express app's load balancer in its region. */
  sharing?: string[];
}

/** The app's platform from its provider and type (spec 18 6.16). */
function platformOf(provider: string | undefined, type: string): string | undefined {
  if (provider === 'aws') return type === 'compute.webapp' ? 'ecs-express' : undefined;
  // Feature spec 17, phase 7: a web app on Google Cloud runs on Cloud Run.
  if (provider === 'gcp') return type === 'compute.webapp' ? 'cloud-run' : undefined;
  if (provider === 'azure') return PLATFORM_OF_TYPE[type];
  return undefined;
}

/**
 * The web and function apps of the deployments attached to the network (App Gateway: in its region). Feature spec 17,
 * phase 8: Cloudflare reaches apps of every deployment the user can see, attached or not (spec 11 6.15 item 39); only an
 * ECS Express app needs its deployment attached in its region (its load balancer group exists only there).
 */
function useOriginOptions(network: Network, kind: EdgeKind, region: string | null) {
  const api = useApi();
  const cloudflare = kind.kind === 'cloudflare';
  const attachments = useAttachments(network.id, {}, null, { limit: 200 });
  const rows = (attachments.data?.items ?? []).filter((a) => a.state !== 'detached');
  const deployments = useQuery({
    queryKey: ['deployments', 'origins'],
    queryFn: () => fetchAllPages((page) => api.listAllDeployments({}, page)),
    enabled: cloudflare,
    staleTime: 30_000,
  });
  const blueprintIds = [
    ...new Set(
      (cloudflare
        ? (deployments.data ?? []).map((d) => d.blueprintId)
        : rows.map((a) => a.blueprintId)
      ).filter(Boolean),
    ),
  ];
  const blueprints = useQueries({
    queries: blueprintIds.map((id) => ({
      queryKey: queryKeys.blueprint(id),
      queryFn: () => api.getBlueprint(id),
      staleTime: 60_000,
    })),
  });
  const loading =
    attachments.isPending || (cloudflare && deployments.isPending) || blueprints.some((b) => b.isPending);
  const options: OriginOption[] = [];
  const seen = new Set<string>();
  if (cloudflare) {
    for (const d of (deployments.data ?? []).filter((x) => x.status !== 'destroyed')) {
      const doc = blueprints.find((b) => b.data?.data.id === d.blueprintId)?.data?.data.content;
      const placed = (doc?.resources ?? []).map((r) => {
        const placement = d.settings.placementOverrides?.[r.id] ?? d.settings.defaultPlacement;
        return { r, placement, platform: platformOf(placement?.provider, r.type) };
      });
      for (const { r, placement, platform } of placed) {
        if (!platform) continue;
        const support = kind.origins.find((o) => o.platform === platform);
        const appRegion = placement?.region ?? '';
        const attachedHere =
          platform !== 'ecs-express' ||
          rows.some((a) => a.deploymentId === d.id && a.provider === 'aws' && a.region === appRegion);
        options.push({
          deploymentId: d.id,
          deploymentName: d.name,
          resourceId: r.id,
          platform,
          label: support?.label ?? platform,
          region: appRegion,
          unavailable: !support
            ? e.codes.ROUTE_ORIGIN_UNKNOWN
            : support.status && support.status !== 'available'
              ? (support.reason?.text ?? support.status)
              : !attachedHere
                ? cf.ecsNotAttached(d.name, network.name, appRegion)
                : null,
          sharing:
            platform === 'ecs-express'
              ? placed
                  .filter(
                    (x) =>
                      x.r.id !== r.id && x.platform === 'ecs-express' && x.placement?.region === appRegion,
                  )
                  .map((x) => x.r.id)
              : undefined,
        });
      }
    }
    return { options, loading };
  }
  for (const a of rows) {
    if (kind.scope === 'regional' && region && a.region !== region) continue;
    const doc = blueprints.find((b) => b.data?.data.id === a.blueprintId)?.data?.data.content;
    for (const r of doc?.resources ?? []) {
      // Feature spec 17, phase 5: a web app attached on AWS runs on ECS Express.
      const platform = platformOf(a.provider, r.type);
      if (!platform) continue;
      const key = `${a.deploymentId}/${r.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const support = kind.origins.find((o) => o.platform === platform);
      options.push({
        deploymentId: a.deploymentId,
        deploymentName: a.deploymentName,
        resourceId: r.id,
        platform,
        label: support?.label ?? platform,
        region: a.region,
        unavailable: !support
          ? e.codes.ROUTE_ORIGIN_UNKNOWN
          : support.status && support.status !== 'available'
            ? (support.reason?.text ?? support.status)
            : null,
      });
    }
  }
  return { options, loading };
}

function Section({ title, children, help }: { title: string; children: ReactNode; help?: string }) {
  return (
    <fieldset className="net-ep-section">
      <legend className="text-[13.5px] font-semibold">{title}</legend>
      {help && <p className="help mt-0 mb-2">{help}</p>}
      {children}
    </fieldset>
  );
}

export function EntryPointForm({
  network,
  kind,
  editing,
  onClose,
  onSaved,
}: {
  network: Versioned<Network>;
  kind: EdgeKind;
  editing: EntryPointSettings | null;
  onClose: () => void;
  onSaved: (message: string, id: string) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const n = network.data;
  const regions = (kind.provider && n.settings.providers[kind.provider]?.regions) || [];
  const [draft, setDraft] = useState<EntryPointSettings>(() => {
    const tier = editing?.tier ?? kind.tiers[0]?.id ?? null;
    return {
      id: editing?.id ?? '',
      label: editing?.label ?? '',
      kind: kind.kind,
      tier,
      ...(kind.scope === 'regional' ? { region: editing?.region ?? regions[0] ?? null } : {}),
      addOns: initialAddOns(kind, editing, tier),
      domains: structuredClone(editing?.domains ?? []),
      routes: structuredClone(editing?.routes ?? []).map((r) => ({
        ...r,
        paths: r.paths?.length ? r.paths : ['/*'],
      })),
    };
  });
  const [pathsText, setPathsText] = useState<string[]>(() =>
    (editing?.routes ?? []).map((r) => (r.paths?.length ? r.paths : ['/*']).join(', ')),
  );
  const origins = useOriginOptions(n, kind, draft.region ?? null);
  const kinds = useNetworkKinds(n.id);
  const cloudFront = kind.kind === 'aws-cloudfront';
  const accelerator = kind.kind === 'aws-global-accelerator';
  const awsEdge = kind.provider === 'aws';
  // Feature spec 17, phase 7: the Google Cloud global external Application Load Balancer.
  const gcpLb = kind.kind === 'gcp-global-load-balancer';
  // Feature spec 17, phase 8: Cloudflare (the zone plan as the tier, a zone per domain, origins attached or not).
  const cloudflare = kind.kind === 'cloudflare';
  // User decision 2026-11-04: rate limiting is offered from Pro (Free's rules can't match host names).
  const cloudflareFree = cloudflare && draft.tier === 'free';
  // Feature spec 17, phase 5: the commitment confirmation (spec 04 6.14.2) and the dialog that collects it.
  const status = useEntryPointStatus(n.id, editing?.id ?? '', !!editing && (awsEdge || gcpLb));
  const connectivityId = kind.provider ? n.cloudAccounts[kind.provider] : undefined;
  const account = useQuery({
    queryKey: queryKeys.cloudAccount(connectivityId ?? ''),
    queryFn: () => api.getCloudAccount(connectivityId!),
    enabled: !!connectivityId && kind.addOns.some((a) => a.commitment),
    staleTime: 60_000,
  });
  const [confirmed, setConfirmed] = useState<{ addOn: string; typed: string } | null>(null);
  const [confirming, setConfirming] = useState<{
    addOn: string;
    variant: 'subscribe' | 'renew';
    expected?: string;
    /** Save once confirmed (a 409 reopened the dialog). */
    thenSave?: boolean;
  } | null>(null);
  const savedOn = (addOnId: string, member: 'enabled' | 'autoRenew') =>
    n.settings.entryPoints.filter((x) => {
      const k = edgeOf(kinds.data, x.kind);
      if (!k?.addOns.some((a) => a.id === addOnId)) return false;
      return member === 'enabled' ? addOnEnabled(x, k, addOnId) : !!x.addOns?.[addOnId]?.autoRenew;
    });
  const index = editing
    ? n.settings.entryPoints.findIndex((x) => x.id === editing.id)
    : n.settings.entryPoints.length;
  const prefix = `/settings/entryPoints/${index}`;

  /** The draft as saved: empty label dropped, route paths from their text. */
  const saved: EntryPointSettings = useMemo(() => {
    const out: EntryPointSettings = { ...draft, label: draft.label?.trim() || null };
    if (!out.label) delete out.label;
    out.routes = (draft.routes ?? []).map((r, i) => ({
      ...r,
      paths: (pathsText[i] ?? '/*')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    }));
    return out;
  }, [draft, pathsText]);

  const listWith = (base: EntryPointSettings[], ep: EntryPointSettings) => {
    const at = base.findIndex((x) => x.id === (editing?.id ?? ep.id));
    if (editing && at >= 0) return base.map((x, i) => (i === at ? ep : x));
    return [...base, ep];
  };
  const patch: PatchNetworkRequest = { settings: { entryPoints: listWith(n.settings.entryPoints, saved) } };
  const patchKey = JSON.stringify(patch);

  // Browser checks (formats and ranges), for responsiveness; the dry run has the server's rules.
  const local: Diagnostic[] = [];
  const add = (path: string, message: string) =>
    local.push({ severity: 'error', path: `${prefix}${path}`, code: 'LOCAL', message });
  if (!ID_PATTERN.test(draft.id)) add('/id', e.idInvalid);
  else if (!editing && n.settings.entryPoints.some((x) => x.id === draft.id)) add('/id', e.idTaken(draft.id));
  const wafId = kind.addOns.find((a) => isWaf(a.id))?.id ?? 'waf';
  const rate = draft.addOns?.[wafId]?.rateLimitPerMinute;
  if (draft.addOns?.[wafId]?.enabled && rate != null && rate !== 0 && (rate < 100 || rate > 100_000))
    add(`/addOns/${wafId}/rateLimitPerMinute`, e.rateLimitInvalid);
  (draft.domains ?? []).forEach((d, i) => {
    if (cloudflare) {
      if (d.dnsZone && !CLOUDFLARE_ZONE_PATTERN.test(d.dnsZone.trim()))
        add(`/domains/${i}/dnsZone`, cf.zoneIdInvalid);
    } else if (d.dnsZone && !ZONE_PATTERN.test(d.dnsZone.trim()))
      add(`/domains/${i}/dnsZone`, aw.dnsZoneInvalid);
  });
  const hosts = (draft.domains ?? []).map((d) => d.hostname.trim().toLowerCase());
  (draft.domains ?? []).forEach((d, i) => {
    if (!HOST_PATTERN.test(d.hostname.trim())) add(`/domains/${i}/hostname`, e.hostInvalid);
    else if (hosts.indexOf(d.hostname.trim().toLowerCase()) !== i)
      add(`/domains/${i}/hostname`, e.hostDuplicate);
    if (d.certificate.kind === 'key-vault' && !d.certificate.secretId?.trim())
      add(`/domains/${i}/certificate/secretId`, e.secretIdRequired);
  });
  (saved.routes ?? []).forEach((r, i) => {
    if (!ID_PATTERN.test(r.id)) add(`/routes/${i}/id`, e.idInvalid);
    if (!r.origin.deploymentId) add(`/routes/${i}/origin`, e.originMissing);
    if (!r.paths?.length || r.paths.some((p) => !PATH_PATTERN.test(p)))
      add(`/routes/${i}/paths`, e.pathInvalid);
  });

  // The dry run (debounced; the last answer wins).
  const [dry, setDry] = useState<{
    key: string;
    state: 'loading' | 'done' | 'error';
    data: NetworkValidation | null;
  } | null>(null);
  const [retry, setRetry] = useState(0);
  const latest = useRef(0);
  useEffect(() => {
    const request = ++latest.current;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setDry((d) => ({ key: patchKey, state: 'loading', data: d?.data ?? null }));
      api
        .validateNetwork(n.id, JSON.parse(patchKey) as PatchNetworkRequest, controller.signal)
        .then((data) => {
          if (request === latest.current) setDry({ key: patchKey, state: 'done', data });
        })
        .catch(() => {
          if (request === latest.current && !controller.signal.aborted)
            setDry((d) => ({ key: patchKey, state: 'error', data: d?.data ?? null }));
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, n.id, patchKey, retry]);

  const [saveDiagnostics, setSaveDiagnostics] = useState<Diagnostic[]>([]);
  const server = dry?.state === 'done' ? (dry.data?.diagnostics ?? []) : [];
  const diagnostics = [...local, ...server.filter((d) => d.path.startsWith(prefix)), ...saveDiagnostics];
  const elsewhere = server.filter((d) => d.severity === 'error' && !d.path.startsWith(prefix));
  const errors = diagnostics.filter((d) => d.severity === 'error');
  const at = (path: string, exact = false) =>
    diagnostics.filter((d) =>
      exact
        ? d.path === `${prefix}${path}`
        : d.path === `${prefix}${path}` || d.path.startsWith(`${prefix}${path}/`),
    );
  const messages = (path: string, exact = false) => at(path, exact).map((d) => d.message);
  const hasCode = (path: string, ...codes: string[]) => at(path).some((d) => codes.includes(d.code));

  // Feature spec 17, phase 8 (spec 11 6.15 item 39): Cloudflare's `CLOUDFLARE_ZONE_IN_USE` shows at the zone field and
  // `CLOUDFLARE_ROUTE_SHAPE` at the route, also when the API reports them at the entry point's own path.
  const placedCodes = ['CLOUDFLARE_ZONE_IN_USE', 'CLOUDFLARE_ROUTE_SHAPE'];
  const unplaced = (code: string) =>
    diagnostics.filter((d) => d.code === code && (d.path === prefix || d.path === `${prefix}/domains`));
  const zoneInUseAt = (i: number) => {
    const zone = draft.domains?.[i]?.dnsZone?.trim();
    if (
      !cloudflare ||
      !zone ||
      at(`/domains/${i}/dnsZone`, true).some((d) => d.code === 'CLOUDFLARE_ZONE_IN_USE')
    )
      return [];
    return unplaced('CLOUDFLARE_ZONE_IN_USE').filter((d) => d.message.includes(zone));
  };
  /** The later of two routes that share a host name with different origins carries the shape error. */
  const routeShapeAt = (i: number) => {
    if (!cloudflare || at(`/routes/${i}`).some((d) => d.code === 'CLOUDFLARE_ROUTE_SHAPE')) return [];
    const all = (draft.domains ?? []).map((d) => d.hostname.trim()).filter(Boolean);
    const hostsOf = (r: NonNullable<EntryPointSettings['routes']>[number]) =>
      r.hostnames?.length ? r.hostnames : all;
    const routes = draft.routes ?? [];
    const route = routes[i];
    const clash = routes
      .slice(0, i)
      .some(
        (other) =>
          (other.origin.deploymentId !== route.origin.deploymentId ||
            other.origin.resourceId !== route.origin.resourceId) &&
          hostsOf(other).some((h) => hostsOf(route).includes(h)),
      );
    return clash ? unplaced('CLOUDFLARE_ROUTE_SHAPE') : [];
  };
  // Placed codes the form couldn't place (no matching zone or route) still show at the top: they block the save.
  const placed = new Set([
    ...(draft.domains ?? []).flatMap((_, i) => zoneInUseAt(i)),
    ...(draft.routes ?? []).flatMap((_, i) => routeShapeAt(i)),
  ]);
  const entryPointLevel = diagnostics.filter(
    (d) =>
      d.severity === 'error' &&
      (d.path === prefix || (placedCodes.includes(d.code) && d.path === `${prefix}/domains`)) &&
      !placed.has(d),
  );

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const save = async (confirmation = confirmed) => {
    if (errors.length) return;
    setBusy(true);
    setMessage(null);
    setSaveDiagnostics([]);
    let target = network;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const body: PatchNetworkRequest = {
            settings: { entryPoints: listWith(target.data.settings.entryPoints, saved) },
            ...(confirmation ? { confirmCommitment: confirmation } : {}),
          };
          const result = await api.patchNetwork(n.id, body, target.etag);
          queryClient.setQueryData(queryKeys.network(n.id), result);
          void queryClient.invalidateQueries({ queryKey: ['network', n.id] });
          void queryClient.invalidateQueries({ queryKey: ['networks'] });
          onSaved(editing ? e.savedEdit(n.name, saved.id) : e.saved(n.name, saved.id), saved.id);
          return;
        } catch (error) {
          if (isApiError(error, 'PRECONDITION_FAILED') && attempt === 0) {
            // Merge flow: take the newer array and put this entry point into it, then send again once.
            target = await api.getNetwork(n.id);
            queryClient.setQueryData(queryKeys.network(n.id), target);
            continue;
          }
          if (isApiError(error, 'PRECONDITION_FAILED')) setMessage(e.conflict);
          else if (isApiError(error, 'COMMITMENT_NOT_CONFIRMED')) {
            // A stale form or a changed name: the dialog reopens with the server's text (spec 11 6.15.33).
            const details = (error.problem as { details?: CommitmentNotConfirmedDetails }).details;
            const addOn = details?.addOn ?? confirmation?.addOn ?? 'shield-advanced';
            setConfirmed(null);
            setConfirming({
              addOn,
              variant:
                (details?.renewalOnly ??
                (!!draft.addOns?.[addOn]?.autoRenew && savedOn(addOn, 'enabled').length > 0))
                  ? 'renew'
                  : 'subscribe',
              expected: details?.expected,
              thenSave: true,
            });
          } else if (isApiError(error) && error.diagnostics.length) setSaveDiagnostics(error.diagnostics);
          else toast.error(error);
          return;
        }
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- updates

  const setAddOn = (id: string, next: Partial<EntryPointAddOnSettings>) =>
    setDraft((d) => {
      const addOns = { ...d.addOns, [id]: { ...(d.addOns?.[id] ?? { enabled: false }), ...next } };
      // The DDoS add-ons exclude each other.
      if (next.enabled && id === 'ddos-ip-protection' && addOns['ddos-network-protection'])
        addOns['ddos-network-protection'] = { ...addOns['ddos-network-protection'], enabled: false };
      if (next.enabled && id === 'ddos-network-protection' && addOns['ddos-ip-protection'])
        addOns['ddos-ip-protection'] = { ...addOns['ddos-ip-protection'], enabled: false };
      return { ...d, addOns };
    });
  const setTier = (tier: string) =>
    setDraft((d) => {
      const addOns = { ...d.addOns };
      for (const a of kind.addOns)
        if (a.requiresTier && addOns[a.id]) {
          // An add-on included in the tier (no own cost) follows it; a paid one stays off until chosen.
          const included = !a.cost && !a.commitment && a.default === false && a.id === 'private-origins';
          addOns[a.id] = {
            ...addOns[a.id],
            enabled: a.requiresTier === tier ? included || addOns[a.id].enabled : false,
          };
        }
      if (cloudflare) {
        // Detection mode needs Enterprise (spec 19 6.3): a lower plan returns the WAF to prevention.
        if (tier !== 'enterprise' && addOns.waf?.mode === 'detection')
          addOns.waf = { ...addOns.waf, mode: 'prevention' };
        // No rate limit on Free (user decision 2026-11-04): Free's rules can't match host names; Pro and higher get 2,000 back.
        if (addOns.waf && tier === 'free') addOns.waf = { ...addOns.waf, rateLimitPerMinute: 0 };
        else if (addOns.waf && d.tier === 'free') addOns.waf = { ...addOns.waf, rateLimitPerMinute: 2000 };
      } else if (tier !== 'premium' && addOns.waf)
        addOns.waf = { ...addOns.waf, managedRules: null, botProtection: null };
      return { ...d, tier, addOns };
    });
  const setDomain = (i: number, next: Partial<NonNullable<EntryPointSettings['domains']>[number]>) =>
    setDraft((d) => ({ ...d, domains: (d.domains ?? []).map((x, j) => (j === i ? { ...x, ...next } : x)) }));
  const setRoute = (i: number, next: Partial<NonNullable<EntryPointSettings['routes']>[number]>) =>
    setDraft((d) => ({ ...d, routes: (d.routes ?? []).map((x, j) => (j === i ? { ...x, ...next } : x)) }));

  const certificates = kind.certificates ?? ['managed', 'key-vault'];
  const domainNames = (draft.domains ?? []).map((d) => d.hostname.trim()).filter(Boolean);

  const costs = (dry?.data?.impact?.costs ?? []).filter((c) => c.change === 'add');
  const costItems = costs.length
    ? costs.map((c) => e.costItem(c.item, c.currency, c.monthly)).join(', ')
    : null;
  const confirmingLabel = confirming
    ? (commitmentOf(kinds.data, kind, confirming.addOn)?.label ?? confirming.addOn)
    : null;
  const commitmentLines = (dry?.data?.impact?.commitments ?? []).map((c) =>
    aw.commitmentLine(c.label, c.currency, c.monthly, c.months),
  );
  // Feature spec 17, phase 8: Cloudflare's cost is the zone plan the entry point relies on (spec 18 6.10).
  const planTier = cloudflare ? kind.tiers.find((t) => t.id === draft.tier) : undefined;
  const costLine =
    cloudflare && planTier
      ? planTier.cost
        ? planTier.cost.monthly === 0
          ? cf.costFree(planTier.label)
          : cf.costLine(planTier.label, planTier.cost.currency, planTier.cost.monthly)
        : `Cloudflare ${planTier.label}: ${cf.priceOnRequest}`
      : dry?.state === 'error'
        ? null
        : confirmingLabel
          ? aw.costsConfirming(costItems, confirmingLabel.replace(/^AWS /, ''))
          : !dry?.data
            ? e.costsPending
            : [
                costs.length ? e.costLine(costItems ?? '', asOfWords(costs[0].asOf)) : e.noFixedCost,
                ...commitmentLines,
              ].join(' · ');

  /** Switching a commitment add-on (or its renewal) on: the dialog first when nothing saved has it on (D13). */
  const toggleCommitment = (addOnId: string, member: 'enabled' | 'autoRenew', on: boolean) => {
    if (!on) {
      setAddOn(addOnId, member === 'enabled' ? { enabled: false, autoRenew: null } : { autoRenew: false });
      if (confirmed?.addOn === addOnId) setConfirmed(null);
      return;
    }
    // Already on in the saved settings (this or another entry point): no new commitment (spec 04 6.14.2).
    if (savedOn(addOnId, member).length > 0) {
      setAddOn(addOnId, member === 'enabled' ? { enabled: true } : { autoRenew: true });
      return;
    }
    setConfirming({ addOn: addOnId, variant: member === 'enabled' ? 'subscribe' : 'renew' });
  };

  // ---------------------------------------------------------------- render

  const field = (path: string) => `${ids}${path.replace(/\//g, '-')}`;
  const describe = (path: string, extra?: string) => {
    const has = messages(path, true).length > 0;
    return [has ? `${field(path)}-err` : null, extra].filter(Boolean).join(' ') || undefined;
  };

  const confirmingCommitment = confirming ? commitmentOf(kinds.data, kind, confirming.addOn) : null;
  return (
    <>
      <Dialog
        title={editing ? e.editTitle(editing.id) : e.newTitle(kind.label)}
        onClose={onClose}
        wide
        className="dialog-sheet net-ep-form"
        footer={
          <div className="flex w-full flex-wrap items-center gap-2">
            <p className="mr-auto text-[12.5px] text-fg-muted" aria-live="polite" data-testid="ep-impact">
              {dry?.state === 'error' ? (
                <>
                  {e.impactUnknown}{' '}
                  <button type="button" className="link" onClick={() => setRetry((x) => x + 1)}>
                    <RefreshCw aria-hidden className="mr-1 inline size-3.5" />
                    {strings.common.retry}
                  </button>
                </>
              ) : dry?.data?.impact ? (
                <span className={dry.state === 'loading' ? 'italic' : undefined}>
                  {dry.data.impact.summary}
                </span>
              ) : (
                <span className="italic">{e.checking}</span>
              )}
              {errors.length > 0 && (
                <span className="block text-[var(--danger)]">{e.fixErrors(errors.length)}</span>
              )}
            </p>
            <button type="button" className="btn" onClick={onClose}>
              {strings.common.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              aria-disabled={errors.length > 0 || busy ? true : undefined}
              onClick={() => !busy && void save()}
            >
              {busy ? e.saving : e.save}
            </button>
          </div>
        }
      >
        {message && <p className="callout callout-warning mb-3">{message}</p>}
        {entryPointLevel.length > 0 && (
          <ul className="callout callout-danger mb-3" data-testid="ep-form-level-errors">
            {entryPointLevel.map((d, i) => (
              <li key={i}>{d.message}</li>
            ))}
          </ul>
        )}
        {elsewhere.length > 0 && (
          <ul className="callout callout-danger mb-3">
            {elsewhere.map((d, i) => (
              <li key={i}>{d.message}</li>
            ))}
          </ul>
        )}
        <Section title={e.name}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor={field('/id')}>
                {e.id}
              </label>
              <input
                id={field('/id')}
                className="input w-full font-mono"
                value={draft.id}
                readOnly={!!editing}
                aria-invalid={messages('/id', true).length > 0 || undefined}
                aria-describedby={describe('/id', `${field('/id')}-help`)}
                onChange={(event) => setDraft((d) => ({ ...d, id: event.target.value }))}
              />
              <p id={`${field('/id')}-help`} className="help">
                {e.idHelp}
              </p>
              <FieldMessages id={`${field('/id')}-err`} messages={messages('/id', true)} />
            </div>
            <div>
              <label className="label" htmlFor={field('/label')}>
                {e.label}
              </label>
              <input
                id={field('/label')}
                className="input w-full"
                value={draft.label ?? ''}
                onChange={(event) => setDraft((d) => ({ ...d, label: event.target.value }))}
              />
            </div>
          </div>
        </Section>

        {kind.tiers.length === 0 && kind.scope !== 'regional' ? (
          // Feature spec 17, phase 5: a kind without tiers has its own cost line instead of a tier control.
          <p className="net-ep-kind-cost" data-testid="ep-kind-cost">
            <span className="font-semibold">{aw.pricing}:</span>{' '}
            {kind.cost
              ? aw.kindCost(gcpLb ? gc.kindCostLabel : kind.label, kind.cost.currency, kind.cost.monthly)
              : aw.usageCost}
          </p>
        ) : (
          <Section title={cloudflare ? cf.plan : e.tier} help={cloudflare ? cf.planHelp : undefined}>
            <div role="radiogroup" aria-label={cloudflare ? cf.plan : e.tier} className="net-ep-tiers">
              {kind.tiers.map((t) => (
                <label key={t.id} className={`net-ep-tier ${draft.tier === t.id ? 'is-checked' : ''}`}>
                  <input
                    type="radio"
                    name={`${ids}-tier`}
                    checked={draft.tier === t.id}
                    onChange={() => setTier(t.id)}
                  />
                  {cloudflare ? (
                    // Feature spec 17, phase 8: the zone plan with its list price per zone; Enterprise has none.
                    <span>
                      <span className="block font-semibold">
                        {t.cost ? cf.planCost(t.label, t.cost.currency, t.cost.monthly) : t.label}
                      </span>
                      <span className="block text-[12px] text-fg-muted">
                        {t.cost ? cf.perZone : cf.priceOnRequest}
                      </span>
                    </span>
                  ) : (
                    <span>
                      <span className="block font-semibold">
                        {t.cost ? e.tierCost(t.label, t.cost.currency, t.cost.monthly) : t.label}
                      </span>
                      {t.id === 'premium' && (
                        <span className="block text-[12px] text-fg-muted">{e.premiumExtras}</span>
                      )}
                    </span>
                  )}
                </label>
              ))}
            </div>
            <FieldMessages messages={messages('/tier', true)} />
            {cloudflare && draft.tier !== 'enterprise' && (
              // Step 8b (spec 11 item 39): below Enterprise the origin serves the route's host names itself.
              <p className="help mt-2" data-testid="ep-cf-plan-origin-note">
                {cf.planOriginNote}
              </p>
            )}
            {kind.scope === 'regional' && (
              <div className="mt-3">
                <label className="label" htmlFor={field('/region')}>
                  {e.region}
                </label>
                <select
                  id={field('/region')}
                  className="input"
                  value={draft.region ?? ''}
                  aria-invalid={messages('/region').length > 0 || undefined}
                  aria-describedby={describe('/region', `${field('/region')}-help`)}
                  onChange={(event) => setDraft((d) => ({ ...d, region: event.target.value }))}
                >
                  {regions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <p id={`${field('/region')}-help`} className="help">
                  {e.regionHelp}
                </p>
                <FieldMessages
                  id={`${field('/region')}-err`}
                  messages={at('/region').map((d) =>
                    d.code === 'AZURE_APP_GATEWAY_HUB_TOO_SMALL'
                      ? e.codes.AZURE_APP_GATEWAY_HUB_TOO_SMALL
                      : d.message,
                  )}
                />
              </div>
            )}
          </Section>
        )}

        {kind.addOns.length > 0 && (
          <Section title={e.addOns}>
            <ul className="space-y-2">
              {kind.addOns.map((a) => {
                const state = draft.addOns?.[a.id] ?? { enabled: false };
                const tierOk = !a.requiresTier || a.requiresTier === draft.tier;
                const needs = tierOk
                  ? null
                  : e.needsTier(
                      kind.tiers.find((t) => t.id === a.requiresTier)?.label ?? a.requiresTier ?? '',
                    );
                const id = field(`/addOns/${a.id}`);
                const commitment = a.commitment ? commitmentOf(kinds.data, kind, a.id) : null;
                const years = commitment ? Math.round(commitment.terms.months / 12) : 0;
                // Feature spec 17, phase 8: Cloudflare's WAF runs the managed ruleset of the plan; DDoS protection is
                // always on; caching off adds a cache bypass rule.
                const alwaysOn = cloudflare && a.id === 'ddos';
                const label = cloudflare
                  ? alwaysOn
                    ? cf.ddos
                    : a.id === 'cdn'
                      ? cf.caching
                      : a.label
                  : a.label;
                const sentence =
                  (cloudflare
                    ? a.id === 'waf'
                      ? draft.tier === 'free'
                        ? cf.managedRulesFree
                        : cf.managedRulesPaid
                      : alwaysOn
                        ? cf.ddosHelp
                        : a.id === 'cdn' && !state.enabled
                          ? cf.cachingHelp
                          : null
                    : null) ??
                  a.byTier?.[draft.tier ?? ''] ??
                  (a.id === 'aws-waf'
                    ? aw.wafUsage
                    : commitment
                      ? aw.commitmentTerms(
                          commitment.terms.currency,
                          commitment.terms.monthly,
                          commitment.terms.months,
                          commitment.terms.per,
                        )
                      : null);
                const cost = a.cost
                  ? a.id === 'cloud-armor'
                    ? gc.armorCost(a.cost.currency, a.cost.monthly)
                    : `${e.addOnCost(a.cost.currency, a.cost.monthly)}${a.id === 'ddos-ip-protection' ? ` ${e.perPublicIp}` : ''}`
                  : null;
                const waf = isWaf(a.id) && state.enabled;
                const awsWaf = a.id === 'aws-waf';
                const cloudArmor = a.id === 'cloud-armor';
                // Feature spec 17, phase 7: an add-on the UI holds back (Cloud Armor Enterprise) is greyed out.
                const held = heldReason(a);
                const extra = [needs, cost].filter(Boolean).join(' · ');
                const described = !!(sentence || extra);
                const infos = at(`/addOns/${a.id}`, true).filter(
                  // The Free plan's ruleset is the WAF's sentence already (CLOUDFLARE_MANAGED_RULES_FREE).
                  (d) => d.severity === 'info' && d.code !== 'CLOUDFLARE_MANAGED_RULES_FREE',
                );
                const savedHere = !!editing && addOnEnabled(editing, kind, a.id);
                const endsAt = status.data?.commitments?.find((c) => c.addOn === a.id)?.endsAt ?? null;
                return (
                  <li
                    key={a.id}
                    className={`net-ep-addon ${held ? 'is-held' : ''}`}
                    data-testid={`ep-addon-${a.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        id={id}
                        type="checkbox"
                        role="switch"
                        checked={alwaysOn || (state.enabled && tierOk && !held)}
                        aria-disabled={needs || held || alwaysOn ? true : undefined}
                        aria-describedby={
                          [described ? `${id}-desc` : null, held ? `${id}-held` : null]
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                        onChange={(event) => {
                          if (needs || held || alwaysOn) return;
                          if (commitment) toggleCommitment(a.id, 'enabled', event.target.checked);
                          else setAddOn(a.id, { enabled: event.target.checked });
                        }}
                      />
                      <label htmlFor={id} className="font-medium">
                        {label}
                      </label>
                      {alwaysOn && <span className="badge tone-neutral">{cf.alwaysOn}</span>}
                      {commitment && (
                        <span className="badge tone-danger" data-testid={`ep-commitment-badge-${a.id}`}>
                          {aw.commitmentBadge(years)}
                        </span>
                      )}
                      {cost && <span className="badge tone-neutral">{cost}</span>}
                      {needs && <span className="badge tone-neutral">{needs}</span>}
                    </div>
                    {held && (
                      <p id={`${id}-held`} className="help mt-0.5" data-testid={`ep-addon-held-${a.id}`}>
                        {held}
                      </p>
                    )}
                    {described && (
                      <p id={`${id}-desc`} className={sentence ? 'help mt-0.5' : 'sr-only'}>
                        {sentence}
                        {extra && <span className="sr-only">{sentence ? ` · ${extra}` : extra}</span>}
                      </p>
                    )}
                    {commitment && !state.enabled && savedHere && (
                      <p className="callout callout-warning mt-1" data-testid={`ep-commitment-off-${a.id}`}>
                        {endsAt ? aw.shieldOff(dayWords(endsAt)) : aw.shieldOffNoDate}
                      </p>
                    )}
                    {commitment && state.enabled && a.id === 'cloud-armor-enterprise-annual' && (
                      // No auto-renew switch: the subscription and its renewal are the billing account's (spec 11 item 37).
                      <p className="help mt-0.5" data-testid={`ep-billing-note-${a.id}`}>
                        {gc.billingNote}
                      </p>
                    )}
                    {infos.map((d) => (
                      <p
                        key={d.code}
                        className="callout callout-info mt-1"
                        data-testid={`ep-addon-info-${d.code}`}
                      >
                        {d.code === 'GCP_ARMOR_ENTERPRISE_SUBSCRIPTION'
                          ? e.codes.GCP_ARMOR_ENTERPRISE_SUBSCRIPTION(n.name)
                          : d.message}
                      </p>
                    ))}
                    {commitment && state.enabled && a.id !== 'cloud-armor-enterprise-annual' && (
                      <div className="net-ep-waf" data-testid={`ep-renewal-${a.id}`}>
                        <label className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            role="switch"
                            checked={!!state.autoRenew}
                            aria-describedby={`${id}-renew-help`}
                            onChange={(event) => toggleCommitment(a.id, 'autoRenew', event.target.checked)}
                          />
                          {aw.autoRenew}
                        </label>
                        <p id={`${id}-renew-help`} className="help mt-0">
                          {state.autoRenew
                            ? endsAt
                              ? aw.renewsOn(dayWords(endsAt))
                              : aw.renewsAfterTerm
                            : `${endsAt ? aw.endsOn(dayWords(endsAt)) : aw.endsAfterTerm} ${aw.autoRenewHelp(commitment.terms.months)}`}
                        </p>
                      </div>
                    )}
                    {waf && (
                      <div className="net-ep-waf">
                        <div role="radiogroup" aria-label={e.wafMode} className="flex flex-wrap gap-3">
                          {(['prevention', 'detection'] as const).map((mode) => {
                            // Feature spec 17, phase 8: Cloudflare's detection mode needs Enterprise (the log action).
                            const off = cloudflare && mode === 'detection' && draft.tier !== 'enterprise';
                            return (
                              <label
                                key={mode}
                                className={`flex items-center gap-1.5 text-sm ${off ? 'is-disabled text-fg-muted' : ''}`}
                              >
                                <input
                                  type="radio"
                                  name={`${ids}-waf-mode`}
                                  checked={(state.mode ?? 'prevention') === mode}
                                  aria-disabled={off || undefined}
                                  aria-describedby={off ? `${id}-detection-off` : undefined}
                                  onChange={() => !off && setAddOn(a.id, { mode })}
                                />
                                {mode === 'prevention' ? e.prevention : e.detection}
                                {off && (
                                  <span id={`${id}-detection-off`} className="text-[12px]">
                                    ({cf.detectionNeedsEnterprise})
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                        {(state.mode ?? 'prevention') === 'detection' && (
                          <p className="help">{e.detectionHelp}</p>
                        )}
                        {cloudflare ? (
                          // Cloudflare (phase 8): the managed ruleset of the plan; bot protection is a zone setting.
                          <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-1.5 text-sm">
                              <input
                                type="checkbox"
                                checked={state.managedRules ?? true}
                                onChange={(event) => setAddOn(a.id, { managedRules: event.target.checked })}
                              />
                              {e.managedRules}
                            </label>
                            <span className="text-[12px] text-fg-muted" data-testid="ep-cf-bot-zone">
                              {cf.botZoneSetting}
                            </span>
                          </div>
                        ) : cloudArmor ? (
                          // Cloud Armor (phase 7): the preconfigured rules; bot management isn't built.
                          <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-1.5 text-sm">
                              <input
                                type="checkbox"
                                checked={state.managedRules ?? true}
                                onChange={(event) => setAddOn(a.id, { managedRules: event.target.checked })}
                              />
                              {gc.managedRules}
                            </label>
                            <span className="text-[12px] text-fg-muted" data-testid="ep-armor-bot-later">
                              {gc.botLater}
                            </span>
                          </div>
                        ) : draft.tier === 'premium' || awsWaf ? (
                          <div className="flex flex-wrap gap-4">
                            {(['managedRules', 'botProtection'] as const).map((key) => (
                              <label key={key} className="flex items-center gap-1.5 text-sm">
                                <input
                                  type="checkbox"
                                  // Both default to true (spec 18 5.1), so an unset value reads as on; AWS WAF's Bot
                                  // Control defaults to off on CloudFront (priced per request, spec 18 6.16).
                                  checked={state[key] ?? !(awsWaf && key === 'botProtection')}
                                  onChange={(event) => setAddOn(a.id, { [key]: event.target.checked })}
                                />
                                {key === 'managedRules'
                                  ? e.managedRules
                                  : awsWaf
                                    ? aw.botControl
                                    : e.botProtection}
                                {awsWaf && key === 'botProtection' && (
                                  <span className="text-[12px] text-fg-muted">({aw.botControlNote})</span>
                                )}
                              </label>
                            ))}
                          </div>
                        ) : kind.tiers.some((t) => t.id === 'premium') ? (
                          <p className="help">{e.managedNeedsPremium}</p>
                        ) : null}
                        <div>
                          <label className="label" htmlFor={field(`/addOns/${a.id}/rateLimitPerMinute`)}>
                            {e.rateLimit}
                          </label>
                          <input
                            id={field(`/addOns/${a.id}/rateLimitPerMinute`)}
                            type="number"
                            min={0}
                            max={100000}
                            className="input w-40"
                            value={cloudflareFree ? '' : (state.rateLimitPerMinute ?? '')}
                            // Feature spec 17, phase 8 (user decision 2026-11-04): no rate limit on Cloudflare Free.
                            readOnly={cloudflareFree}
                            aria-disabled={cloudflareFree || undefined}
                            aria-describedby={describe(
                              `/addOns/${a.id}/rateLimitPerMinute`,
                              `${field(`/addOns/${a.id}/rateLimitPerMinute`)}-help`,
                            )}
                            onChange={(event) =>
                              cloudflareFree
                                ? undefined
                                : setAddOn(a.id, {
                                    rateLimitPerMinute:
                                      event.target.value === '' ? null : Number(event.target.value),
                                  })
                            }
                          />
                          <p id={`${field(`/addOns/${a.id}/rateLimitPerMinute`)}-help`} className="help">
                            {cloudflareFree ? cf.rateLimitFree : e.rateLimitHelp}
                          </p>
                          <FieldMessages
                            id={`${field(`/addOns/${a.id}/rateLimitPerMinute`)}-err`}
                            messages={messages(`/addOns/${a.id}/rateLimitPerMinute`, true)}
                          />
                        </div>
                      </div>
                    )}
                    <FieldMessages
                      messages={[
                        ...at(`/addOns/${a.id}`, true),
                        // Feature spec 17, phase 8: the mode's and `enabled`'s refusals on Cloudflare.
                        ...(cloudflare
                          ? [...at(`/addOns/${a.id}/mode`, true), ...at(`/addOns/${a.id}/enabled`, true)]
                          : []),
                      ]
                        .filter((d) => d.severity !== 'info')
                        .map((d) =>
                          d.code === 'COMMITMENT_NOT_CONFIRMED'
                            ? aw.staysOff(a.label)
                            : d.code === 'CLOUDFLARE_WAF_DETECTION_NEEDS_ENTERPRISE'
                              ? e.codes.CLOUDFLARE_WAF_DETECTION_NEEDS_ENTERPRISE
                              : d.message,
                        )}
                    />
                    <FieldMessages
                      messages={at(`/addOns/${a.id}/botProtection`, true).map((d) =>
                        d.code === 'ADD_ON_NOT_AVAILABLE' && cloudArmor
                          ? e.codes.ADD_ON_NOT_AVAILABLE_BOT
                          : d.message,
                      )}
                    />
                  </li>
                );
              })}
            </ul>
            {kind.addOns.some((a) => a.id === 'ddos-ip-protection') && (
              <p className="help">{e.ddosExclusive}</p>
            )}
          </Section>
        )}

        {accelerator ? (
          // Global Accelerator takes no domains (TLS passthrough, spec 11 6.15.33).
          <Section title={e.domains}>
            <p className="help mt-0" data-testid="ep-passthrough">
              {aw.passthrough}
            </p>
            <FieldMessages
              messages={at('/domains')
                .map(() => aw.passthrough)
                .slice(0, 1)}
            />
          </Section>
        ) : (
          <Section
            title={e.domains}
            help={gcpLb ? gc.domainRequired : cloudflare ? cf.domainsHelp : e.domainsHelp}
          >
            {kind.kind === 'azure-app-gateway' && <p className="help mt-0 mb-2">{e.keyVaultOnly}</p>}
            {gcpLb && <p className="help mt-0 mb-2">{gc.managedOnly}</p>}
            <FieldMessages
              messages={at('/domains', true).map((d) =>
                d.code === 'ENTRY_DOMAIN_REQUIRED' && gcpLb ? e.codes.ENTRY_DOMAIN_REQUIRED : d.message,
              )}
            />
            <ul className="space-y-2">
              {(draft.domains ?? []).map((d, i) => (
                <li key={i} className="net-ep-row" data-testid={`ep-form-domain-${i}`}>
                  <div className="min-w-0 flex-1">
                    <label className="sr-only" htmlFor={field(`/domains/${i}/hostname`)}>
                      {e.domainHost(i + 1)}
                    </label>
                    <input
                      id={field(`/domains/${i}/hostname`)}
                      className="input w-full font-mono"
                      placeholder="shop.example.com"
                      value={d.hostname}
                      aria-invalid={messages(`/domains/${i}/hostname`, true).length > 0 || undefined}
                      aria-describedby={describe(`/domains/${i}/hostname`)}
                      onChange={(event) => setDomain(i, { hostname: event.target.value })}
                    />
                    <FieldMessages
                      id={`${field(`/domains/${i}/hostname`)}-err`}
                      messages={at(`/domains/${i}/hostname`, true)
                        .filter((x) => x.code !== 'CLOUDFLARE_CERTIFICATE_NOT_COVERED')
                        .map((x) => x.message)}
                    />
                    {cloudflare &&
                      at(`/domains/${i}`)
                        .filter((x) => x.code === 'CLOUDFLARE_CERTIFICATE_NOT_COVERED')
                        .slice(0, 1)
                        .map((x) => (
                          // Feature spec 17, phase 8: a warning, the save goes on.
                          <p
                            key={x.code}
                            className="callout callout-warning mt-1 text-[12.5px]"
                            data-testid={`ep-cf-cert-warning-${i}`}
                          >
                            {e.codes.CLOUDFLARE_CERTIFICATE_NOT_COVERED(d.hostname.trim())}
                          </p>
                        ))}
                  </div>
                  {cloudflare ? (
                    // Feature spec 17, phase 8: no certificate choice, Cloudflare's edge certificate serves the domain.
                    <p className="help mt-2 shrink-0" data-testid={`ep-cf-certificate-${i}`}>
                      {cf.edgeCertificate}
                    </p>
                  ) : (
                    <div>
                      <select
                        className="input"
                        aria-label={e.domainCertificate(i + 1)}
                        value={d.certificate.kind}
                        onChange={(event) =>
                          setDomain(i, {
                            certificate: {
                              kind: event.target.value as 'managed' | 'key-vault',
                              secretId: d.certificate.secretId,
                            },
                          })
                        }
                      >
                        {certificates.map((c) => (
                          <option key={c} value={c}>
                            {c === 'managed' ? e.managed : e.keyVault}
                          </option>
                        ))}
                      </select>
                      <FieldMessages messages={messages(`/domains/${i}/certificate/kind`, true)} />
                    </div>
                  )}
                  {(cloudFront || cloudflare) && (
                    <div className="min-w-0 flex-1">
                      <label className="sr-only" htmlFor={field(`/domains/${i}/dnsZone`)}>
                        {cloudflare ? cf.zoneId(i + 1) : aw.dnsZone(i + 1)}
                      </label>
                      <input
                        id={field(`/domains/${i}/dnsZone`)}
                        className="input w-full font-mono"
                        aria-required={cloudflare || undefined}
                        placeholder={
                          cloudflare ? cf.zoneIdShort : aw.dnsZone(i + 1).replace(/ of domain \d+/, '')
                        }
                        value={d.dnsZone ?? ''}
                        aria-invalid={messages(`/domains/${i}/dnsZone`, true).length > 0 || undefined}
                        aria-describedby={describe(
                          `/domains/${i}/dnsZone`,
                          `${field(`/domains/${i}/dnsZone`)}-help`,
                        )}
                        onChange={(event) =>
                          setDomain(i, {
                            dnsZone: event.target.value.trim() ? event.target.value.trim() : null,
                          })
                        }
                      />
                      <p id={`${field(`/domains/${i}/dnsZone`)}-help`} className="help">
                        {cloudflare ? cf.zoneIdHelp : aw.dnsZoneHelp}
                      </p>
                      <FieldMessages
                        id={`${field(`/domains/${i}/dnsZone`)}-err`}
                        messages={[...at(`/domains/${i}/dnsZone`, true), ...zoneInUseAt(i)].map((x) =>
                          x.code === 'DNS_ZONE_NOT_SUPPORTED'
                            ? e.codes.DNS_ZONE_NOT_SUPPORTED
                            : x.code === 'DNS_ZONE_REQUIRED'
                              ? e.codes.DNS_ZONE_REQUIRED(d.hostname.trim())
                              : x.message,
                        )}
                      />
                    </div>
                  )}
                  {d.certificate.kind === 'key-vault' && (
                    <div className="min-w-0 flex-1">
                      <input
                        id={field(`/domains/${i}/certificate/secretId`)}
                        className="input w-full font-mono"
                        placeholder="https://<vault>.vault.azure.net/secrets/<name>"
                        aria-label={e.secretId(i + 1)}
                        value={d.certificate.secretId ?? ''}
                        aria-describedby={describe(`/domains/${i}/certificate/secretId`)}
                        onChange={(event) =>
                          setDomain(i, { certificate: { kind: 'key-vault', secretId: event.target.value } })
                        }
                      />
                      <FieldMessages
                        id={`${field(`/domains/${i}/certificate/secretId`)}-err`}
                        messages={messages(`/domains/${i}/certificate/secretId`, true)}
                      />
                    </div>
                  )}
                  <IconButton
                    label={e.removeDomain(d.hostname)}
                    onClick={() =>
                      setDraft((x) => ({ ...x, domains: (x.domains ?? []).filter((_, j) => j !== i) }))
                    }
                  >
                    <Trash2 aria-hidden className="size-4" />
                  </IconButton>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn btn-sm mt-2"
              onClick={() =>
                setDraft((x) => ({
                  ...x,
                  domains: [
                    ...(x.domains ?? []),
                    { hostname: '', certificate: { kind: certificates[0] ?? 'managed' } },
                  ],
                }))
              }
            >
              <Plus aria-hidden className="size-4" />
              {e.addDomain}
            </button>
            {cloudflare && (
              <p className="help mt-2" data-testid="ep-cf-ssl-note">
                {cf.sslNote}
              </p>
            )}
          </Section>
        )}

        <Section title={e.routes} help={e.routesHelp}>
          {!origins.loading && origins.options.length === 0 && (
            <p className="help">{cloudflare ? cf.noOrigins : e.noOrigins(n.name)}</p>
          )}
          <ol className="space-y-3">
            {(draft.routes ?? []).map((r, i) => {
              const option = origins.options.find(
                (o) => o.deploymentId === r.origin.deploymentId && o.resourceId === r.origin.resourceId,
              );
              const platform = option?.platform ?? null;
              const support = platform ? kind.origins.find((o) => o.platform === platform) : undefined;
              const offered = platform ? offeredModes(draft, kind, platform) : [];
              const preselected = platform ? preselectedMode(draft, kind, platform) : null;
              const chosen: OriginAccessMode | null =
                r.originAccess && r.originAccess !== 'auto' ? r.originAccess : preselected;
              const modes = [
                ...ACCESS_ORDER.filter((m) => (support?.access as string[] | undefined)?.includes(m)),
                'none' as const,
              ];
              const pushShown =
                r.allowPlatformPush !== undefined ||
                hasCode(
                  `/routes/${i}`,
                  'ORIGIN_PLATFORM_PUSH_NEEDS_CHOICE',
                  'ORIGIN_LOCKDOWN_ALLOWS_PLATFORM',
                );
              const grouped = new Map<string, OriginOption[]>();
              for (const o of origins.options)
                grouped.set(o.deploymentName, [...(grouped.get(o.deploymentName) ?? []), o]);
              const originErrors = at(`/routes/${i}/origin`).map((d) =>
                d.code === 'ROUTE_ORIGIN_NOT_ATTACHED'
                  ? e.codes.ROUTE_ORIGIN_NOT_ATTACHED(option?.deploymentName ?? d.message, n.name)
                  : d.code === 'ROUTE_ORIGIN_UNKNOWN'
                    ? e.codes.ROUTE_ORIGIN_UNKNOWN
                    : d.message,
              );
              return (
                <li key={i} className="net-ep-route" data-testid={`ep-form-route-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-[13px] font-semibold">{e.route(i + 1)}</h4>
                    <IconButton
                      label={e.removeRoute(r.id)}
                      onClick={() => {
                        setDraft((x) => ({ ...x, routes: (x.routes ?? []).filter((_, j) => j !== i) }));
                        setPathsText((p) => p.filter((_, j) => j !== i));
                      }}
                    >
                      <Trash2 aria-hidden className="size-4" />
                    </IconButton>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="label" htmlFor={field(`/routes/${i}/id`)}>
                        {e.routeId(i + 1)}
                      </label>
                      <input
                        id={field(`/routes/${i}/id`)}
                        className="input w-full font-mono"
                        value={r.id}
                        aria-describedby={describe(`/routes/${i}/id`)}
                        onChange={(event) => setRoute(i, { id: event.target.value })}
                      />
                      <FieldMessages
                        id={`${field(`/routes/${i}/id`)}-err`}
                        messages={messages(`/routes/${i}/id`, true)}
                      />
                    </div>
                    <fieldset>
                      <legend className="label">{e.routeHosts(i + 1)}</legend>
                      {awsEdge ? (
                        // Feature spec 17, phase 5: CloudFront routes by path, Global Accelerator by region.
                        <>
                          <p className="help mt-0" data-testid={`ep-form-paths-only-${i}`}>
                            {accelerator ? aw.regionRoute(option?.region ?? '…') : aw.pathsOnly}
                          </p>
                          <FieldMessages
                            id={`${field(`/routes/${i}/hostnames`)}-err`}
                            messages={at(`/routes/${i}/hostnames`, true).map((d) =>
                              d.code === 'ROUTE_SHAPE_NOT_SUPPORTED'
                                ? e.codes.ROUTE_SHAPE_NOT_SUPPORTED
                                : d.message,
                            )}
                          />
                        </>
                      ) : domainNames.length === 0 ? (
                        <p className="help mt-0">{kind.defaultHost ? e.edgeOwnHost : e.allDomains}</p>
                      ) : (
                        <>
                          {cloudflare && (
                            // Feature spec 17, phase 8: one origin per host name (CLOUDFLARE_ROUTE_SHAPE).
                            <FieldMessages
                              id={`${field(`/routes/${i}/hostnames`)}-err`}
                              messages={[
                                ...at(`/routes/${i}`, false).filter(
                                  (d) => d.code === 'CLOUDFLARE_ROUTE_SHAPE',
                                ),
                                ...routeShapeAt(i),
                              ].map((d) => d.message)}
                            />
                          )}
                          <p className="help mt-0">
                            {(r.hostnames ?? []).length === 0 ? e.allDomains : null}
                          </p>
                          {domainNames.map((h) => (
                            <label key={h} className="flex items-center gap-1.5 text-sm">
                              <input
                                type="checkbox"
                                checked={(r.hostnames ?? []).includes(h)}
                                onChange={(event) =>
                                  setRoute(i, {
                                    hostnames: event.target.checked
                                      ? [...(r.hostnames ?? []), h]
                                      : (r.hostnames ?? []).filter((x) => x !== h),
                                  })
                                }
                              />
                              <code className="font-mono text-[12px]">{h}</code>
                            </label>
                          ))}
                        </>
                      )}
                    </fieldset>
                    <div>
                      <label className="label" htmlFor={field(`/routes/${i}/paths`)}>
                        {e.routePaths(i + 1)}
                      </label>
                      <input
                        id={field(`/routes/${i}/paths`)}
                        className="input w-full font-mono"
                        // Global Accelerator routes by region: the path stays /* (spec 11 6.15.33).
                        readOnly={accelerator}
                        value={accelerator ? '/*' : (pathsText[i] ?? '/*')}
                        aria-describedby={describe(
                          `/routes/${i}/paths`,
                          `${field(`/routes/${i}/paths`)}-help`,
                        )}
                        onChange={(event) =>
                          setPathsText((p) => {
                            const next = [...p];
                            next[i] = event.target.value;
                            return next;
                          })
                        }
                      />
                      <p id={`${field(`/routes/${i}/paths`)}-help`} className="help">
                        {e.pathsHelp}
                      </p>
                      <FieldMessages
                        id={`${field(`/routes/${i}/paths`)}-err`}
                        messages={at(`/routes/${i}/paths`, true).map((d) =>
                          d.code === 'ROUTE_SHAPE_NOT_SUPPORTED' ? e.codes.ROUTE_SHAPE_REGION : d.message,
                        )}
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="label" htmlFor={field(`/routes/${i}/origin`)}>
                      {e.routeOrigin(i + 1)}
                    </label>
                    <select
                      id={field(`/routes/${i}/origin`)}
                      className="input w-full sm:w-auto"
                      value={r.origin.deploymentId ? `${r.origin.deploymentId}/${r.origin.resourceId}` : ''}
                      aria-invalid={originErrors.length > 0 || undefined}
                      aria-describedby={
                        originErrors.length ? `${field(`/routes/${i}/origin`)}-err` : undefined
                      }
                      onChange={(event) => {
                        const [deploymentId, resourceId] = event.target.value.split('/');
                        setRoute(i, {
                          origin: { deploymentId: deploymentId ?? '', resourceId: resourceId ?? '' },
                          originAccess: 'auto',
                          allowPlatformPush: undefined,
                        });
                      }}
                    >
                      <option value="">{e.chooseOrigin}</option>
                      {[...grouped].map(([deployment, apps]) => (
                        <optgroup key={deployment} label={deployment}>
                          {apps.map((o) => (
                            <option
                              key={`${o.deploymentId}/${o.resourceId}`}
                              value={`${o.deploymentId}/${o.resourceId}`}
                              disabled={!!o.unavailable}
                            >
                              {`${o.deploymentName} / ${o.resourceId} · ${o.label}${
                                o.unavailable
                                  ? ` (${o.unavailable})`
                                  : awsEdge || gcpLb || cloudflare
                                    ? (() => {
                                        const m = preselectedMode(draft, kind, o.platform);
                                        return m
                                          ? ` · ${(cloudFront && aw.accessLabel[m]) || (cloudflare && cf.accessLabel[m]) || e.accessLabel[m]}`
                                          : o.platform === 'cloud-run'
                                            ? ` · ${gc.secretHeaderOnly}`
                                            : '';
                                      })()
                                    : o.platform === 'cloud-run'
                                      ? // Feature spec 17, phase 7: Front Door reaches Cloud Run with the secret header only.
                                        ` · ${gc.secretHeaderOnly}`
                                      : ''
                              }`}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {kind.scope === 'regional' && draft.region && (
                      <p className="help">{e.inRegion(draft.region)}</p>
                    )}
                    <FieldMessages id={`${field(`/routes/${i}/origin`)}-err`} messages={originErrors} />
                  </div>
                  {platform && (
                    <fieldset className="mt-2" data-testid={`ep-form-access-${i}`}>
                      <legend className="label">{e.routeAccess(i + 1)}</legend>
                      {support?.default === null && <p className="help mt-0">{e.mustChoose}</p>}
                      <div role="radiogroup" aria-label={e.routeAccess(i + 1)} className="space-y-1">
                        {modes.map((m) => {
                          const available = m === 'none' || offered.includes(m);
                          const descId = `${field(`/routes/${i}/originAccess`)}-${m}`;
                          return (
                            <label key={m} className={`net-ep-access ${available ? '' : 'is-disabled'}`}>
                              <input
                                type="radio"
                                name={`${ids}-access-${i}`}
                                checked={chosen === m}
                                aria-disabled={available ? undefined : true}
                                aria-describedby={descId}
                                onChange={() => {
                                  if (!available) return;
                                  setRoute(i, { originAccess: m === preselected ? 'auto' : m });
                                }}
                              />
                              <span>
                                <span className="font-medium">
                                  {(cloudFront && aw.accessLabel[m]) ||
                                    (cloudflare && cf.accessLabel[m]) ||
                                    e.accessLabel[m]}
                                </span>{' '}
                                <span className="text-[12px] text-fg-muted">
                                  ({e.strength[ACCESS_STRENGTH[m]]})
                                </span>
                                <span id={descId} className="block text-[12px] text-fg-muted">
                                  {(() => {
                                    // Feature spec 17, phase 8: Cloudflare's lock-downs with what the app must do.
                                    if (cloudflare && available) {
                                      const app = r.origin.resourceId;
                                      if (m === 'allow-list-and-header')
                                        return `${cf.accessSentence[m]} ${cf.appCheck(app)}`;
                                      if (m === 'header')
                                        return `${cf.accessSentence.header} ${cf.appCheck(app)}`;
                                      if (m === 'none') return cf.notLocked(app);
                                    }
                                    const sentence = (awsEdge && aw.accessSentence[m]) || e.accessSentence[m];
                                    if (available) return sentence;
                                    return m === 'vpc-origin'
                                      ? `${sentence} ${aw.needsVpcOrigins}.`
                                      : `${sentence} ${e.needsTier('Premium with private origins')}.`;
                                  })()}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {cloudflare &&
                        option?.platform === 'ecs-express' &&
                        chosen !== 'none' &&
                        (option.sharing ?? [])
                          .filter(
                            (other) =>
                              !(draft.routes ?? []).some(
                                (x) =>
                                  x.origin.deploymentId === option.deploymentId &&
                                  x.origin.resourceId === other,
                              ),
                          )
                          .map((other) => (
                            <p
                              key={other}
                              className="callout callout-warning mt-1 text-[12.5px]"
                              data-testid={`ep-cf-shared-alb-${i}`}
                            >
                              {e.codes.AWS_SHARED_ALB_LOCKED(
                                other,
                                kind.label,
                                option.region,
                                option.resourceId,
                              )}
                            </p>
                          ))}
                      <FieldMessages
                        messages={at(`/routes/${i}/originAccess`, true)
                          // Cloudflare's lock-down warnings are the radios' sentences (spec 11 6.15 item 39).
                          .filter(
                            (d) =>
                              !cloudflare ||
                              !['ORIGIN_LOCKDOWN_APP_CHECK', 'ORIGIN_LOCKDOWN_UNAVAILABLE'].includes(d.code),
                          )
                          .map((d) =>
                            d.code === 'AWS_VPC_ORIGIN_NEEDS_PRIVATE_SUBNETS'
                              ? e.codes.AWS_VPC_ORIGIN_NEEDS_PRIVATE_SUBNETS(r.origin.resourceId, n.name)
                              : d.message,
                          )}
                      />
                      {hasCode(`/routes/${i}/originAccess`, 'AWS_VPC_ORIGIN_NEEDS_PRIVATE_SUBNETS') && (
                        <Link
                          className="link mt-1 inline-block text-[12.5px]"
                          to={`/networks/${n.id}?tab=settings#providers`}
                          data-testid={`ep-form-switch-nat-${i}`}
                        >
                          {aw.switchToNat}
                        </Link>
                      )}
                    </fieldset>
                  )}
                  {pushShown && (
                    <div className="mt-2">
                      <label className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={!!r.allowPlatformPush}
                          aria-describedby={`${field(`/routes/${i}/allowPlatformPush`)}-help`}
                          onChange={(event) => setRoute(i, { allowPlatformPush: event.target.checked })}
                        />
                        {e.push}
                      </label>
                      <p id={`${field(`/routes/${i}/allowPlatformPush`)}-help`} className="help">
                        {e.pushHelp(r.origin.resourceId)}
                      </p>
                      <FieldMessages
                        messages={at(`/routes/${i}/allowPlatformPush`, true)
                          .filter((d) => d.severity === 'error')
                          .map((d) => d.message)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
          <button
            type="button"
            className="btn btn-sm mt-2"
            onClick={() => {
              const routes = draft.routes ?? [];
              setDraft((x) => ({
                ...x,
                routes: [
                  ...(x.routes ?? []),
                  {
                    id: routes.length ? `route-${routes.length + 1}` : 'web',
                    hostnames: [],
                    paths: ['/*'],
                    origin: { deploymentId: '', resourceId: '' },
                    originAccess: 'auto',
                  },
                ],
              }));
              setPathsText((p) => [...p, '/*']);
            }}
          >
            <Plus aria-hidden className="size-4" />
            {e.addRoute}
          </button>
        </Section>

        <p className="net-ep-costs" data-testid="ep-costs">
          <span className="font-semibold">{e.costs}:</span> {costLine ?? e.impactUnknown}
        </p>
        {cloudflare && (dry?.data?.impact?.deploymentsToApply.length ?? 0) > 0 && (
          // Feature spec 17, phase 8: the origins whose own apply locks them to Cloudflare after the network's.
          <p className="net-ep-costs" data-testid="ep-origins-to-apply">
            <span className="font-semibold">{strings.networks.topology.deploymentsToApply}:</span>{' '}
            {dry!.data!.impact!.deploymentsToApply.map((x) => `${x.name} (${x.reason})`).join(', ')}
          </p>
        )}
      </Dialog>
      {confirming && confirmingCommitment && (
        <CommitmentDialog
          network={n.name}
          label={confirmingCommitment.label}
          terms={confirmingCommitment.terms}
          account={account.data?.data.name ?? null}
          expected={confirming.expected ?? expectedConfirmation(n.name, confirmingCommitment.terms)}
          variant={confirming.variant}
          endsAt={status.data?.commitments?.find((c) => c.addOn === confirming.addOn)?.endsAt ?? null}
          billingAccount={confirming.addOn === 'cloud-armor-enterprise-annual'}
          onCancel={() => setConfirming(null)}
          onConfirm={(typed) => {
            const confirmation = { addOn: confirming.addOn, typed };
            setAddOn(
              confirming.addOn,
              confirming.variant === 'renew' ? { autoRenew: true } : { enabled: true },
            );
            setConfirmed(confirmation);
            setConfirming(null);
            if (confirming.thenSave) void save(confirmation);
          }}
        />
      )}
    </>
  );
}
